// src/components/dashboards/composition/blocks.ts
//
// The block contract of a self-built dashboard, in one place (plan
// 2026-09-02 composition builder, C4). Every producer and consumer of a block
// reads its shape here: the palette, the canvas, the tile renderers and the
// page that persists them.
//
// A block IS a dashboard item. The block type lives in `chart_spec_json`, so
// no new column and no new endpoint is needed:
//
//   kpi / chart / table, not configured yet
//     { chart_type: "placeholder", block_type: "kpi" | "chart" | "table",
//       visualization?: "<registry key>" }     (the Insert gallery's pick)
//     no query_ir_json, no data_source_id
//   text
//     { chart_type: "text", content: string }      (markdown, max 4000 chars)
//   heading
//     { chart_type: "heading", content: string }   (one line)
//   smart_text (plan 2026-10-16, ADR-0426)
//     { chart_type: "smart_text", content, language, variables,
//       compare, columns, metric_formats }   (the last three stamped by the server)
//     query_ir_json derived from the variables (one row)
//   shape (a line, plan 2026-09-25)
//     { chart_type: "shape", shape: { kind, orientation, thickness_px, color } }
//     no query_ir_json, no data_source_id
//   analysis / agent_deep_dive (a report only, plan 2026-10-24 D2b/D10)
//     { chart_type: "analysis", analysis: { kind, max_lines?, bullets? },
//       scope?, reading?, reads?: [block_key], instruction? }
//     { chart_type: "agent_deep_dive", agent_deep_dive: { max_deep_dives,
//       recommendation }, scope?, reading?, reads?, instruction? }
//     no query_ir_json, no data_source_id
//
// Once a data block is configured, its spec is a normal chart spec and the
// tile renders like any other insight.
import { COMPOSITION_LAYOUT } from "@verity/shared-types/composition-layout";
import { COMPOSITION_SETTINGS } from "@verity/shared-types/composition-settings";
import {
  CHART_STYLES as CHART_STYLES_VALUE,
  KPI_CHART_TYPES,
  POINT_SPLIT_BREAKDOWN_CHART_TYPES,
  STRUCTURAL_SECOND_DIMENSION_CHART_TYPES,
} from "@verity/shared-types/chart-styles";
import type { DashboardChartType as DashboardChartTypeValue } from "@verity/shared-types/chart-styles";
import { BLOCK_INTENTS } from "@/api/composition";
import type { BlockType, VisualizationSurface } from "@/api/composition";
import { DATA_BLOCK_TYPES } from "@/api/composition";
import type { Visualization } from "@/api/composition";
import type { ShapeSpec } from "@verity/report-components";

export type DataBlockType = "kpi" | "chart" | "table";
export type TextBlockType = "text" | "heading";

/** Order the palette offers the blocks in: data first, layout second, and the
 *  report-only reading of the data last. */
export const BLOCK_ORDER: readonly BlockType[] = [
  "kpi", "chart", "table", "text", "smart_text", "heading", "analysis", "agent_deep_dive", "shape", "control",
  // Template-only (plan 2026-10-13, C3): in the register, in no Insert menu.
  "anchors", "bridge", "cohort_returns", "attribution_table", "follow_up",
];

/** The surfaces a block can be placed on — the register's tag, read off the
 *  generated spec so a new row cannot be offered on a surface the backend
 *  would refuse (plan 2026-09-08-report-editor-is-the-report-itself, A3). */
export type BlockSurface = "dashboard" | "report";

export function blockSurfaces(blockType: BlockType): readonly BlockSurface[] {
  const row = (COMPOSITION_LAYOUT.blocks as Record<string, { surfaces?: readonly string[] }>)[blockType];
  return (row?.surfaces ?? []) as readonly BlockSurface[];
}

/** What the Insert menu of one surface offers, in `BLOCK_ORDER`. Both
 *  surfaces offer the markdown text block: it was left off the dashboard on
 *  2026-09-02 and brought back on 2026-10-05 (Arjen; plan dashboard edit row
 *  separate text and line), behind the edit row's T menu next to Heading. */
export function offeredBlockTypes(surface: BlockSurface): readonly BlockType[] {
  return BLOCK_ORDER.filter((t) => blockSurfaces(t).includes(surface) && blockIsOffered(t));
}

/** False for a block only Verity's templates place (the control): the
 *  register sizes and renders it, no Insert menu lists it. Read off the spec,
 *  like `blockSurfaces`; a row without the flag is offered. */
export function blockIsOffered(blockType: BlockType): boolean {
  const row = (COMPOSITION_LAYOUT.blocks as Record<string, { offered?: boolean }>)[blockType];
  return row?.offered !== false;
}

/** What the dashboard's edit panel offers. */
export const OFFERED_BLOCK_TYPES: readonly BlockType[] = offeredBlockTypes("dashboard");

/** What the dashboard editor's Insert offers on a row of this kind (ADR-0496
 *  D1: the surface of an item write is the dashboard's kind). The register
 *  decides: a report offers its intelligence blocks (analysis, agent deep
 *  dive) since they have a stored item form, the run that fills them and
 *  their panel (plan 2026-10-24 intelligence behind the edition, D10); a
 *  dashboard offers none, because the register gives them the report
 *  surface only. */
export function editorBlockTypes(kind: BlockSurface): readonly BlockType[] {
  return offeredBlockTypes(kind);
}

/** What a block IS, as the register tags it: a visual the reader looks at
 *  (KPI, chart, table, text, heading) or a piece of intelligence the platform
 *  writes (analysis, agent deep dive). Read off the generated spec exactly as
 *  `blockSurfaces` is, so the split the report's two Insert menus draw is the
 *  backend's split and not a second, hand-kept copy of it (plan
 *  2026-09-09-reports-intelligence, Decision 7). */
export type BlockCategory = "visual" | "intelligence";

/** The category the register gives this block type, or null when the spec
 *  names none — the honest read, which is what the drift guard asserts
 *  against. Grouping does NOT go through this null: see below. */
export function blockCategory(blockType: BlockType): BlockCategory | null {
  const row = (COMPOSITION_LAYOUT.blocks as Record<string, { category?: string }>)[blockType];
  const category = row?.category;
  return category === "visual" || category === "intelligence" ? category : null;
}

/** The chart type each report-only block stores, mapped back to its block
 *  type: the register's `stored_chart_type` (D2b,
 *  `services/composition/report_items.py::STORED_CHART_TYPE`), never a copy
 *  typed here. */
export const REPORT_ITEM_BLOCK_TYPE_OF_CHART_TYPE: Readonly<Record<string, BlockType>> = Object.fromEntries(
  Object.entries(COMPOSITION_LAYOUT.blocks as Record<string, { stored_chart_type?: string }>)
    .filter(([, row]) => typeof row.stored_chart_type === "string")
    .map(([blockType, row]) => [row.stored_chart_type as string, blockType as BlockType]),
);

/** The two report blocks an author places and a model writes for (D10). */
export type IntelligenceBlockType = "analysis" | "agent_deep_dive";

export function isIntelligenceBlockType(value: unknown): value is IntelligenceBlockType {
  return value === "analysis" || value === "agent_deep_dive";
}

/** An analysis or agent deep dive item: its chart type names it. */
export function isIntelligenceSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return isIntelligenceBlockType(spec?.chart_type);
}

/** Spec of a new intelligence block: the chart type and the server's own
 *  defaults (`AnalysisSpec`: a narrative; `AgentDeepDiveSpec`: one deep dive,
 *  no recommendation). Nothing scoped, nothing read: the whole report. */
export function newIntelligenceSpec(blockType: IntelligenceBlockType): Record<string, unknown> {
  return blockType === "analysis"
    ? { chart_type: "analysis", analysis: { kind: "narrative" } }
    : { chart_type: "agent_deep_dive", agent_deep_dive: { max_deep_dives: 1, recommendation: false } };
}

/** The blocks the platform writes. */
export const INTELLIGENCE_BLOCK_TYPES: readonly BlockType[] = BLOCK_ORDER.filter(
  (t) => blockCategory(t) === "intelligence",
);

/** The blocks the builder places and fills — everything that is not
 *  intelligence. Deliberately the COMPLEMENT rather than a second equality
 *  test: the two lists then partition `BLOCK_ORDER` by construction, so a
 *  type whose spec forgot its category is still offered somewhere instead of
 *  vanishing from every menu while every test stays green. The drift guard
 *  goes red on the missing category itself. */
export const VISUAL_BLOCK_TYPES: readonly BlockType[] = BLOCK_ORDER.filter(
  (t) => blockCategory(t) !== "intelligence",
);

/** What one surface offers, split by category and each half in `BLOCK_ORDER`.
 *  The report's Insert menus are built from this, so a new intelligence row in
 *  the register lands in the intelligence menu with no frontend change. */
export function offeredBlockTypesByCategory(
  surface: BlockSurface,
): Record<BlockCategory, readonly BlockType[]> {
  const offered = offeredBlockTypes(surface);
  return {
    visual: offered.filter((t) => VISUAL_BLOCK_TYPES.includes(t)),
    intelligence: offered.filter((t) => INTELLIGENCE_BLOCK_TYPES.includes(t)),
  };
}

// ---------------------------------------------------------------------------
// The size spec (plan 2026-09-02 block size spec and row layouts, Decision 3)
// ---------------------------------------------------------------------------
//
// The frontend no longer owns how big a block is. `services/composition/layout.py`
// does, and `scripts/emit_composition_layout.py` emits it into the shared-types
// package at build time (`make generate-types`, drift-checked in CI). This
// module is the ONLY place that imports the generated object, so every consumer
// keeps reading one table and none of them can drift from the backend that
// validates the write.

export type GridSize = { w: number; h: number };

/** Columns of the dashboard grid. */
export const GRID_COLUMNS: number = COMPOSITION_LAYOUT.columns;
/** Height of one grid row, in pixels. */
export const GRID_ROW_HEIGHT_PX: number = COMPOSITION_LAYOUT.row_height_px;
/** Gap between two tiles, in pixels, horizontally and vertically. */
export const GRID_GAP_PX: number = COMPOSITION_LAYOUT.gap_px;

/** The emitted size of a VISUALIZATION ROW, for the rows whose picture needs
 *  a different cell than their block type gives them (plan W4.3). Absent for
 *  every other row, and for a spelling this build does not know, so both
 *  lookups below then read the block type exactly as before. Mirrors
 *  `services/composition/layout.py::block_size_for`.
 *
 *  KEY OR SPELLING. The emitted table is keyed by registry KEY, which is what
 *  a placeholder stores (`visualization: "funnel_explorer"`); a configured
 *  tile hands in its stored `chart_type` spelling. The backend resolves a
 *  spelling to its key; this build has no static spelling map, and it needs
 *  none yet because every sized row's spelling IS its key
 *  (`blocks.sizing.test.ts` pins that, so the first row where they differ
 *  goes red here instead of silently losing its minimum). */
export function visualizationCellSize(visualization: string | null | undefined) {
  if (!visualization) return null;
  const rows = COMPOSITION_LAYOUT.visualizations as
    | Record<string, { default: GridSize; min: GridSize }>
    | undefined;
  return rows?.[visualization] ?? null;
}

/** Size a new block of this type is created at, in grid units. The row wins
 *  when it carries its own size: a funnel is a chart block that a chart
 *  block's 6x8 clips. */
export function blockDefaultSize(
  blockType: BlockType,
  visualization?: string | null,
): GridSize {
  const row = visualizationCellSize(visualization);
  if (row) return { w: row.default.w, h: row.default.h };
  const spec = COMPOSITION_LAYOUT.blocks[blockType];
  return { w: spec.default.w, h: spec.default.h };
}

/** Smallest size the grid may resize a block to. The backend refuses anything
 *  under it, so the resize handle must stop there — at the ROW's number when
 *  it has one, or the write says no where the handle said yes. */
export function blockMinSize(
  blockType: BlockType,
  visualization?: string | null,
): GridSize {
  const row = visualizationCellSize(visualization);
  if (row) return { w: row.min.w, h: row.min.h };
  const spec = COMPOSITION_LAYOUT.blocks[blockType];
  return { w: spec.min.w, h: spec.min.h };
}

/** Name a new block carries until the builder renames it. */
export function newItemName(blockType: BlockType): string {
  return COMPOSITION_LAYOUT.blocks[blockType].new_item_name;
}

const PROVISIONAL_NAMES = new Set<string>(
  Object.values(COMPOSITION_LAYOUT.blocks).map((b) => b.new_item_name),
);

/** Whether a name is one the SERVER gave the block ("New KPI"), not the
 *  builder. Every reader that prefers `item.name` over a derived label treats
 *  such a name as absent, and the editor treats it as "not named yet" so the
 *  first run names the block after its metric (Arjen, 05-09: a block may
 *  never show "New KPI"). Backend counterpart:
 *  `services/composition/layout.py::is_provisional_name`, same list. */
export function isProvisionalName(name: string | null | undefined): boolean {
  return PROVISIONAL_NAMES.has((name ?? "").trim());
}

/** One row template the drawer offers, as emitted by the backend. */
export type RowTemplateSpec = {
  key: string;
  label: string;
  description: string;
  height: number;
  /** Allowed block counts; empty for a template with a fixed shape. */
  counts: readonly number[];
  default_count: number | null;
  /** The surfaces this row may be offered on, read off the generated spec the
   *  way `blockSurfaces` is. */
  surfaces: readonly BlockSurface[];
};

/** Every row layout the backend can materialise, in the order it lists them.
 *
 *  Prefer `offeredRowTemplates(surface)`: this list is both surfaces' rows
 *  together, and the toolbar is shared (the report editor passes
 *  `offeredBlockTypes("report")` for the block menu and mapped THIS list
 *  unfiltered for the row menu until 2026-09-19). */
export const ROW_TEMPLATES: readonly RowTemplateSpec[] = COMPOSITION_LAYOUT.rows.map(
  (row) => ({
    key: row.key,
    label: row.label,
    description: row.description,
    height: row.height,
    counts: row.counts,
    default_count: ((row as { default_count: number | null }).default_count),
    surfaces: ((row as { surfaces?: readonly string[] }).surfaces ?? []) as readonly BlockSurface[],
  }),
);

/** One slot of a page recipe: a row of data blocks, or a written reading of
 *  the ones before it. The editor needs it to SAY what a section holds; it
 *  needs none of it to place one, because the server does that. */
export type SectionSlotSpec = {
  kind: "row" | "analysis";
  height: number;
  blocks: readonly { block_type: string; w: number }[];
  /** `kind: "row"` only. */
  template?: string;
  /** `kind: "analysis"` only. */
  analysis_kind?: string;
  title?: string;
  items?: number | null;
};

/** One page recipe: the section shapes the house style has already thought
 *  about (`services/composition/page_recipes.py`).
 *
 *  Emitted with the layout spec on purpose: the editor's *Insert section*
 *  menu, the composer's `add_section` and the route that writes one all read
 *  this one registry, so a section a person inserts and a section the model
 *  writes are the same shape. Report surface only — a recipe is a page of
 *  paper, and a dashboard tab has none. */
export type PageSectionSpec = {
  key: string;
  label: string;
  description: string;
  /** What the whole section costs in grid rows, heading included. */
  height: number;
  /** How many blocks the author will fill in afterwards. */
  data_blocks: number;
  slots: readonly SectionSlotSpec[];
};

export const PAGE_SECTIONS: readonly PageSectionSpec[] =
  ((COMPOSITION_LAYOUT as { sections?: readonly PageSectionSpec[] }).sections ??
    []) as readonly PageSectionSpec[];

/** The row layouts ONE surface offers. The same rule block types already
 *  follow (ADR-0242 rule 3): the backend tags the row, the menu filters on the
 *  tag, so a row added for a report page cannot appear in the dashboard's
 *  Insert menu and vice versa. */
export function offeredRowTemplates(surface: BlockSurface): readonly RowTemplateSpec[] {
  return ROW_TEMPLATES.filter((row) => row.surfaces.includes(surface));
}

/** First free grid row under everything on a tab.
 *
 *  One implementation for the canvas's "Add" button and for the page's own
 *  bottom placement: two copies of this arithmetic is how a block added from
 *  the drawer and a block added from the canvas ended up on different rows.
 *
 *  On a free-layout dashboard a block can end between grid lines (3.57); the
 *  next block then starts on the next whole row (4), never on 3 under the
 *  block's last 0.57 (plan 2026-10-11 free block layout, D8.4). The sum is
 *  rounded to the 0.01 grid before the ceil, so float noise (`2.3 + 4.6` is
 *  `6.8999999999999995`) cannot push a whole bottom a row further down. */
export function bottomOfTab(
  items: readonly { layout_y: number; layout_h: number }[],
): number {
  return items.reduce(
    (lowest, item) => Math.max(lowest, Math.ceil(Math.round((item.layout_y + item.layout_h) * 100) / 100)),
    0,
  );
}

export const BLOCK_LABELS: Record<BlockType, string> = {
  kpi: "KPI",
  chart: "Chart",
  table: "Table",
  text: "Text",
  smart_text: "Smart text",
  heading: "Heading",
  analysis: "Analysis",
  agent_deep_dive: "Agent deep dive",
  shape: "Line",
  control: "Control",
  anchors: "Anchors",
  bridge: "Revenue bridge",
  cohort_returns: "Returns by order week",
  attribution_table: "Attribution rules",
  follow_up: "Last period's actions",
};

export const BLOCK_DESCRIPTIONS: Record<BlockType, string> = {
  kpi: "One number, with a comparison.",
  chart: "A trend or a breakdown.",
  table: "Rows and columns.",
  text: "A note in markdown.",
  smart_text: "A sentence with live numbers that follow the period.",
  heading: "A title above a group of blocks.",
  analysis: "A written reading of the blocks it names.",
  agent_deep_dive: "The agent picks what deserves a closer look and shows it with a chart and a table.",
  shape: "A horizontal or vertical line between blocks.",
  control: "A dropdown that changes what the blocks on this page show.",
  anchors: "Month and year to date against last year, and the days to the next peak.",
  bridge: "How visits, conversion rate and order value moved revenue.",
  cohort_returns: "The share of revenue refunded, by the week the order was placed.",
  attribution_table: "Revenue per channel under several attribution rules.",
  follow_up: "Last period's actions, each with the figure it named then and now.",
};

/** The stored chart type of a smart text tile; mirrors `SMART_TEXT_CHART_TYPE`
 *  in `services/composition/blocks.py`. */
export const SMART_TEXT_CHART_TYPE = "smart_text";

/** A text block's markdown is capped so one tile cannot carry a document. */
export const TEXT_BLOCK_MAX_LENGTH = 4000;

export function isBlockType(value: unknown): value is BlockType {
  return (
    typeof value === "string" &&
    (BLOCK_ORDER as readonly string[]).includes(value)
  );
}

/** The fixed layer (plan 2026-10-13, C3/C4): placed by Verity's templates
 *  only, drawn by the server when the report is built. The canvas shows a
 *  recipe for them and the Insert menu never offers them. */
export const FIXED_LAYER_BLOCK_TYPES: readonly BlockType[] = [
  "anchors", "bridge", "cohort_returns", "attribution_table", "follow_up",
];

export function isFixedLayerBlock(value: unknown): value is BlockType {
  return typeof value === "string" && (FIXED_LAYER_BLOCK_TYPES as readonly string[]).includes(value);
}

export function isDataBlockType(value: unknown): value is DataBlockType {
  return typeof value === "string" && (DATA_BLOCK_TYPES as readonly string[]).includes(value);
}

export function isTextBlockType(value: unknown): value is TextBlockType {
  return value === "text" || value === "heading";
}

/** Spec of a data block that has not been configured yet. `visualization`
 *  is the registry KEY picked in the dashboard's Insert gallery (plan
 *  2026-10-05 dashboard insert visualization gallery, D4); it is written only
 *  when given, so a placeholder from a row template or the report stays the
 *  bare two-key spec the backend has always accepted. */
export function placeholderSpec(
  blockType: DataBlockType,
  visualization?: string | null,
): Record<string, unknown> {
  return visualization
    ? { chart_type: "placeholder", block_type: blockType, visualization }
    : { chart_type: "placeholder", block_type: blockType };
}

/** What is being placed from the Insert menu (ADR-0214): the block type and,
 *  when it was picked from the dashboard's visualization gallery (plan
 *  2026-10-05, D3), the registry key of that picture and its label.
 *
 *  `label` is display-only, captured at the pick so the ghost and the
 *  placement hint can say "Donut" without every component on the way
 *  holding the registry. The KEY is what gets written and what sizes the
 *  ghost (`blockDefaultSize(blockType, visualization)`, D6). */
export type Placing = {
  blockType: BlockType;
  visualization?: string | null;
  label?: string | null;
  /** A line only: which way it is drawn, chosen in the edit row's Line menu
   *  (plan 2026-10-05 dashboard edit row separate text and line). Absent is
   *  horizontal, the default every line had before. */
  orientation?: "horizontal" | "vertical" | null;
  /** A control only: which kind of open control, chosen in the edit row's
   *  **Add a control** menu (ADR-0350, plan 2026-09-29 controls A6). It
   *  writes the spec (`newControlSpec`) and sizes the cell
   *  (`controlKindSize`). */
  controlKind?: OpenControlKind | null;
};

/** The cell a block being placed is created at: a line by its orientation
 *  (`shapeDefaultSize`), a control by its kind (`controlKindSize`), every
 *  other block by its type or its picked row (`blockDefaultSize`). One
 *  function, so the ghost, the click and Enter-to-bottom cannot size the
 *  same pick three ways. */
export function placingSize(placing: Placing): GridSize {
  if (placing.blockType === "shape" && placing.orientation) return shapeDefaultSize(placing.orientation);
  if (placing.blockType === "control" && placing.controlKind) return controlKindSize(placing.controlKind);
  return blockDefaultSize(placing.blockType, placing.visualization);
}

/** What the size rule reads a spec as: a placeholder's picked visualization
 *  (it is sized as the picture it will become), else its `chart_type`. The
 *  mirror of `services/composition/blocks.py::sizing_style_of_spec`, so the
 *  resize handle stops where the backend's write check does. */
export function sizingStyleOfSpec(spec: Record<string, unknown> | null | undefined): string | null {
  if (!spec) return null;
  if (spec.chart_type === "placeholder") {
    const key = spec.visualization;
    return typeof key === "string" && key ? key : null;
  }
  return typeof spec.chart_type === "string" ? spec.chart_type : null;
}

/** Spec of a text or heading block. */
export function textBlockSpec(
  blockType: TextBlockType,
  content: string,
): Record<string, unknown> {
  return { chart_type: blockType, content };
}

/** The chart styles a `chart` block renders as, and the union derived from
 *  them. Both live in `@verity/shared-types/chart-styles` since 2026-09-08
 *  (plan one-drawing-layer, A2) and are re-exported here so every existing
 *  importer of `blocks.ts` is unchanged: `@verity/charts` keys its recipe map
 *  on `DashboardChartType` — which is what makes a new registry row a compile
 *  error — and a package that may not import from `apps/` cannot key on a type
 *  declared in this file, which imports the HTTP client above.
 *
 *  The backend mirror test (`test_composition_block_type_resolution.py`) now
 *  parses `CHART_STYLES` out of the shared-types file and `TABLE_CHART_TYPES`
 *  (below) out of this one. */
export { CHART_STYLES, KPI_CHART_TYPES } from "@verity/shared-types/chart-styles";

/** A KPI tile drawn as a registry picture (a bullet, a gauge) rather than the
 *  metric card. Mirrors `KPI_CHART_TYPES` in services/composition/blocks.py
 *  (plan 2026-10-05 finance chart types, D1(a)): such a tile is a KPI block,
 *  so it gets the KPI minimum and reopens in the KPI editor. */
export function isKpiChartType(value: unknown): boolean {
  return value === "metric_card" || (typeof value === "string" && (KPI_CHART_TYPES as readonly string[]).includes(value));
}
export type { DashboardChartType } from "@verity/shared-types/chart-styles";

/**
 * Narrowing guard for a string that may name a chart style. Use this instead
 * of `as DashboardChartType`: the cast it replaces would happily assert a
 * style the union does not have.
 */
export function isDashboardChartType(value: unknown): value is DashboardChartTypeValue {
  return typeof value === "string" && (CHART_STYLES_VALUE as readonly string[]).includes(value);
}

/**
 * Readability cap on a categorical chart: the most categories a bar, pie or
 * donut is asked for. Mirrors `api/constants/visualizations.py::
 * CATEGORICAL_ROW_CAP`, which is the same number the report hook uses as
 * `MAX_CATEGORICAL_ROWS` — one cap for both surfaces (plan 2026-09-05 more
 * chart types, D7). Stamped as the IR's `limit` when the block carries none,
 * never on a time series, a table or a KPI.
 */
export const CATEGORICAL_ROW_CAP = 50;

/**
 * Readability cap on a breakdown: the most series a chart is split into; the
 * rest is drawn as "Other". Mirrors `BREAKDOWN_SERIES_CAP` in
 * `api/constants/visualizations.py` (plan 2026-09-03 a-self-built-chart-
 * cannot-show-a-breakdown, D4) — one number for the tile and the report; the
 * backend's TS-mirror test parses this number against the registry's.
 */
export const BREAKDOWN_SERIES_CAP = 8;

/**
 * What a categorical chart WITH a breakdown asks for when it names no
 * `limit`: categories × series, the report hook's own number. Mirrors
 * `BREAKDOWN_ROW_CAP`; stamped by the editor where `CATEGORICAL_ROW_CAP` is.
 */
export const BREAKDOWN_ROW_CAP = CATEGORICAL_ROW_CAP * BREAKDOWN_SERIES_CAP;

/** The surfaces a row draws a breakdown on. The generated type carries the
 *  field as optional (a Pydantic default), and a row from an older server
 *  answers none: read it as "no breakdown", never as a crash under
 *  `--strict`. */
function breakdownSurfacesOf(v: Visualization): readonly string[] {
  return v.breakdown_surfaces ?? [];
}

/** The row's dimension cap ON A SURFACE: its own, plus one when it draws a
 *  breakdown there (`breakdown_surfaces`, plan 2026-09-03 breakdown, D2) —
 *  the server's `max_dimensions_on(v, surface)`. Null = no cap.
 *
 *  THE SURFACE IS NOT DECORATION (plan 2026-09-09 the-report-block-panel-is-
 *  the-dashboards-panel, Besluit 7). It was hardcoded to "dashboard" while
 *  the dashboard was the only caller; the report's cap is smaller for four of
 *  the nine chart rows it offers — `line` and `bar_over_time` allow 1 there
 *  and 0 here, `bar` and `bar_horizontal` 2 and 1 — because those rows draw a
 *  breakdown on the dashboard only. A caller that asks the dashboard's
 *  question on a report calls a row usable that the server refuses with 422. */
export function dimensionCap(v: Visualization, surface: VisualizationSurface): number | null {
  if (v.max_dimensions == null) return null;
  return v.max_dimensions + (breakdownSurfacesOf(v).includes(surface) ? 1 : 0);
}

/** Whether a query with this many dimensions uses the row's breakdown
 *  allowance on `surface` — one more than the row's own cap (the server's
 *  `uses_breakdown`). Such a query splits exactly one metric, except on the
 *  scatter family: ask `breakdownTakesOneMetric` for the metric rule. */
export function usesBreakdownAllowance(
  v: Visualization,
  dimensionCount: number,
  surface: VisualizationSurface,
): boolean {
  return (
    v.max_dimensions != null &&
    breakdownSurfacesOf(v).includes(surface) &&
    dimensionCount === v.max_dimensions + 1
  );
}

/** Whether a row's breakdown splits the POINTS into series and keeps every
 *  metric: the scatter family (plan 2026-10-16-a-seasonal-heatmap-and-a-
 *  series-per-year, D3). The served registry row does not carry the
 *  backend's `breakdown_splits_points`, so it is read by stored chart type
 *  from `POINT_SPLIT_BREAKDOWN_CHART_TYPES`, which a backend test pins equal
 *  to `api/constants/visualizations.py::point_split_breakdown_chart_types`. */
export function breakdownSplitsPoints(chartType: string | null | undefined): boolean {
  return (POINT_SPLIT_BREAKDOWN_CHART_TYPES as readonly string[]).includes(chartType ?? "");
}

/** Whether this query, on this row and surface, spends the row's breakdown
 *  allowance on a split of ONE metric - the rule the forms enforce ("Takes 1
 *  metric with a breakdown"). `usesBreakdownAllowance` minus the scatter
 *  family, whose breakdown colours its points and keeps every metric (the
 *  server's `uses_breakdown(...) and not v.breakdown_splits_points`,
 *  `services/composition/visualizations.py`). */
export function breakdownTakesOneMetric(
  v: Visualization,
  dimensionCount: number,
  surface: VisualizationSurface,
): boolean {
  return usesBreakdownAllowance(v, dimensionCount, surface) && !breakdownSplitsPoints(v.dashboard_chart_type);
}

/**
 * The query shape an IR describes — the TS twin of
 * `api/constants/visualizations.py::ir_shape`: the explicit `intent` when it
 * is one of the three the registry knows, otherwise a grain AND a time column
 * together mean a series, and everything else is a breakdown.
 */
export function intentOfIr(ir: Record<string, unknown>): "single-value" | "time-series" | "categorical" {
  if (ir.intent === "single-value" || ir.intent === "time-series" || ir.intent === "categorical") {
    return ir.intent;
  }
  const timeKey = typeof ir.time_dimension_key === "string" ? ir.time_dimension_key : "";
  return ir.time_grain && timeKey !== "" ? "time-series" : "categorical";
}

/**
 * The RESULT COLUMN of the dimension a breakdown chart splits its one metric
 * by, from the query alone — the TS twin of `breakdown_column_for` in
 * `api/constants/visualizations.py` (plan 2026-09-03 breakdown, D1), which
 * the write rule compares a saved `breakdown_key` against exactly. A series
 * over time: the one dimension that is not the time key; a breakdown by
 * dimension: the second one (the first is the X axis); null when the query
 * has no such dimension or more than one metric. `alias || key`: what the
 * rows are keyed by.
 */
export function breakdownColumnOf(ir: Record<string, unknown> | null | undefined): string | null {
  if (!ir) return null;
  const metrics = Array.isArray(ir.metrics) ? ir.metrics : [];
  if (metrics.length !== 1) return null;
  const dims = (Array.isArray(ir.dimensions) ? ir.dimensions : []).filter(
    (d): d is Record<string, unknown> =>
      typeof d === "object" && d !== null && typeof (d as Record<string, unknown>).key === "string" && (d as Record<string, unknown>).key !== "",
  );
  let chosen: Record<string, unknown> | undefined;
  if (intentOfIr(ir) === "time-series") {
    const timeKey = typeof ir.time_dimension_key === "string" ? ir.time_dimension_key : "";
    const rest = dims.filter((d) => d.key !== timeKey);
    if (rest.length !== 1) return null;
    chosen = rest[0];
  } else {
    if (dims.length !== 2) return null;
    chosen = dims[1];
  }
  const alias = typeof chosen.alias === "string" ? chosen.alias : "";
  return alias || (chosen.key as string);
}

/**
 * `breakdownColumnOf`, for a spec that says what it draws - the TS twin of
 * `breakdown_column_for_chart` in `api/constants/visualizations.py`, which
 * the item write rule compares a saved `breakdown_key` against. Null for a
 * chart whose second dimension is structural (a sankey's "to"). For the
 * scatter family (`breakdownSplitsPoints`) the second of exactly two
 * dimensions that are not the time key, WHATEVER the metric count: its
 * breakdown colours the points and every metric stays an axis or the size
 * (plan 2026-10-16 seasonal heatmap, D3). Everything else: the IR-only rule.
 */
export function breakdownColumnForChart(
  chartType: string | null | undefined,
  ir: Record<string, unknown> | null | undefined,
): string | null {
  if ((STRUCTURAL_SECOND_DIMENSION_CHART_TYPES as readonly string[]).includes(chartType ?? "")) return null;
  if (!breakdownSplitsPoints(chartType)) return breakdownColumnOf(ir);
  if (!ir) return null;
  const timeKey =
    intentOfIr(ir) === "time-series" && typeof ir.time_dimension_key === "string" ? ir.time_dimension_key : null;
  const dims = (Array.isArray(ir.dimensions) ? ir.dimensions : []).filter(
    (d): d is Record<string, unknown> =>
      typeof d === "object" &&
      d !== null &&
      typeof (d as Record<string, unknown>).key === "string" &&
      (d as Record<string, unknown>).key !== "" &&
      (d as Record<string, unknown>).key !== timeKey,
  );
  if (dims.length !== 2) return null;
  const alias = typeof dims[1].alias === "string" ? dims[1].alias : "";
  return alias || (dims[1].key as string);
}

/**
 * The chart visualization a query gets when nobody chose one: a series over
 * time is a line; a breakdown is a bar, or a grouped bar once it carries more
 * than one metric.
 *
 * The TS twin of `api/constants/visualizations.py::shape_default`, which the
 * chat fill, the MCP tools and the write rule all go through — so the
 * configurator's reopen of a tile whose stored style does not fit its query
 * lands on the SAME visualization the server would have picked. Returns a
 * registry KEY (`bar_grouped`), not a stored chart type.
 */
export function shapeDefaultVisualization(intent: unknown, metricCount: number): string {
  if (intent === "time-series") return "line";
  return metricCount >= 2 ? "bar_grouped" : "bar";
}

/** The chart types a `table` block renders as: rows by dimension (`table`,
 *  InlineTableRenderer) or rows per period (`kpi_period_table`,
 *  KpiPeriodTableRenderer). Mirrors `TABLE_CHART_TYPES` in
 *  services/composition/blocks.py, which derives it from the registry; the
 *  IR's intent picks between them (plan 2026-09-03 table block options).
 *
 *  Pinned across the language boundary since 2026-09-08 by the same backend
 *  test that pins `CHART_STYLES` — until then both halves were hand-kept and
 *  nothing compared them, so a table row added to one side and not the other
 *  cost a tile its size minimum in silence. Exported for that test to read;
 *  `as const` for the same reason `CHART_STYLES` is. */
export const TABLE_CHART_TYPES = ["table", "kpi_period_table", "pivot_table"] as const;

/**
 * The block type a spec describes, or null when the spec names none.
 *
 * The SAME mapping the backend applies (`services/composition/blocks.py::
 * block_type_of_spec`): a placeholder reports the block it is waiting to
 * become, text and heading report themselves, and a CONFIGURED tile reports
 * the block it renders as - `metric_card` is a KPI, `table` and
 * `kpi_period_table` a table, `line` and `bar` a chart, `shape` a line block,
 * `control` a control block. Null only for a legacy chart type that names no block
 * (funnel, pie, horizontal-bar); such a tile has no size minimum on the
 * backend either, so the grid gives it none.
 *
 * Consumers must not read "not null" as "still a placeholder": that question
 * is `isPlaceholderSpec`, and "is this text" is `isTextBlockSpec`.
 */
export function blockTypeOfSpec(spec: Record<string, unknown> | null | undefined): BlockType | null {
  const chartType = spec?.chart_type;
  if (chartType === "placeholder") {
    return isDataBlockType(spec?.block_type) ? spec.block_type : null;
  }
  // The report-only blocks name their own type, through the stored spelling
  // the register emits (`revenue_bridge` is the fixed bridge; a `bridge`
  // chart type is the bridge VISUALIZATION, a chart).
  if (typeof chartType === "string" && chartType in REPORT_ITEM_BLOCK_TYPE_OF_CHART_TYPE) {
    return REPORT_ITEM_BLOCK_TYPE_OF_CHART_TYPE[chartType];
  }
  if (isTextBlockType(chartType)) return chartType;
  if (chartType === SMART_TEXT_CHART_TYPE) return "smart_text";
  if (chartType === SHAPE_CHART_TYPE) return "shape";
  if (chartType === CONTROL_CHART_TYPE) return "control";
  if (isKpiChartType(chartType)) return "kpi";
  if (typeof chartType === "string" && (TABLE_CHART_TYPES as readonly string[]).includes(chartType)) return "table";
  if (isDashboardChartType(chartType)) return "chart";
  return null;
}

// ---------------------------------------------------------------------------
// The line block (plan 2026-09-25 line block, D2/D3)
// ---------------------------------------------------------------------------

/** The stored chart type of a shape tile; mirrors `SHAPE_CHART_TYPE` in
 *  services/composition/blocks.py. */
export const SHAPE_CHART_TYPE = "shape";

/** The line every new line block starts as, on both surfaces: horizontal,
 *  1px, the rule colour. The dashboard stores it under `chart_spec_json.shape`,
 *  a report block under `spec_json.shape`. */
export const DEFAULT_LINE_SHAPE: Readonly<ShapeSpec> = {
  kind: "line",
  orientation: "horizontal",
  thickness_px: 1,
  color: "rule",
};

/** Spec of a new line tile: the default line, drawn the way the author
 *  picked (horizontal unless said otherwise). No query, ever. */
export function newShapeSpec(orientation: "horizontal" | "vertical" = "horizontal"): Record<string, unknown> {
  return { chart_type: SHAPE_CHART_TYPE, shape: { ...DEFAULT_LINE_SHAPE, orientation } };
}

export function isShapeSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return spec?.chart_type === SHAPE_CHART_TYPE;
}

/** The cell a line of this orientation is created at: the register's shape
 *  default for a horizontal one (a full-width row), `vertical_line` for a
 *  vertical one (one column, four rows). Both are the backend's numbers
 *  (`layout.py`, `resolve_size(..., orientation=)`). */
export function shapeDefaultSize(orientation: "horizontal" | "vertical"): GridSize {
  const size = orientation === "vertical" ? COMPOSITION_LAYOUT.vertical_line : COMPOSITION_LAYOUT.blocks.shape.default;
  return { w: size.w, h: size.h };
}

/**
 * The cell a line gets when its orientation flips (D3): the new orientation's
 * default, but only while the cell is still the OLD orientation's default -
 * a size the author chose is theirs and stays. Null = leave the layout alone.
 * `x` is pulled left when the new width would run past the grid, which the
 * backend refuses.
 */
export function shapeLayoutOnOrientationSwitch(
  from: "horizontal" | "vertical",
  to: "horizontal" | "vertical",
  layout: { layout_x: number; layout_w: number; layout_h: number },
): { layout_x: number; layout_w: number; layout_h: number } | null {
  if (from === to) return null;
  const old = shapeDefaultSize(from);
  if (layout.layout_w !== old.w || layout.layout_h !== old.h) return null;
  const next = shapeDefaultSize(to);
  return {
    layout_x: Math.min(layout.layout_x, Math.max(GRID_COLUMNS - next.w, 0)),
    layout_w: next.w,
    layout_h: next.h,
  };
}

// ---------------------------------------------------------------------------
// The control block (plan 2026-09-21 control block, Besluit 1)
// ---------------------------------------------------------------------------

/** The stored chart type of a control tile; mirrors `CONTROL_CHART_TYPES` in
 *  services/composition/blocks.py. Its spec is
 *  `{ chart_type: "control", control: { kind, selection, dimension, label } }`
 *  with NO query: a control draws no data, it steers the tiles beside it. */
export const CONTROL_CHART_TYPE = "control";

export function isControlSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return spec?.chart_type === CONTROL_CHART_TYPE;
}

function controlOfSpec(spec: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!isControlSpec(spec)) return null;
  const control = spec?.control;
  if (typeof control !== "object" || control === null || Array.isArray(control)) return null;
  return control as Record<string, unknown>;
}

/** The selections an OPEN control stores (ADR-0350; backend
 *  `controls.OPEN_CONTROL_SELECTIONS`, enforced on every write). A register
 *  control stores "single_required" (ADR-0323), so the stored spec alone
 *  tells the two families apart. Keyed on the open side: a stored control
 *  that is not explicitly open keeps the register routing it always had. */
function isOpenSelection(selection: unknown): selection is "single" | "multi" {
  return selection === "single" || selection === "multi";
}

/** The keys of the control register (ADR-0323). Mirrors the keys of the
 *  backend's `services/dashboard_controls.py::CONTROL_DIMENSIONS`, which
 *  refuses any of them as an open control's field;
 *  `test_composition_block_type_resolution.py` fails when the two differ. The settings panel's field picker leaves
 *  these out whether or not a register control is on the page. `amount_basis`
 *  is a query option, not a dimension: the filter bar's switch carries it and
 *  no control may name it. */
export const REGISTER_CONTROL_DIMENSIONS = ["attribution_model", "amount_basis"] as const;

/** The dimension a REGISTER control tile steers (the attribution model), or
 *  null when the spec is not a register control or names none. Read off the
 *  stored spec, so a control whose DTO `control` came back null (a dimension
 *  the backend no longer knows) still counts as steering its dimension: its
 *  value must not leak onto tiles that do not carry it.
 *
 *  An OPEN control (ADR-0350) is null here: its field is an ordinary viewer
 *  field, and counting it as a control dimension would send chart clicks on
 *  that field to the URL and drop them on every tile. */
export function controlDimensionOfSpec(spec: Record<string, unknown> | null | undefined): string | null {
  const control = controlOfSpec(spec);
  if (!control || isOpenSelection(control.selection)) return null;
  const dimension = control.dimension;
  return typeof dimension === "string" && dimension !== "" ? dimension : null;
}

/** The kinds of open control that steer a field (backend `DIMENSION_KINDS`). */
export const OPEN_CONTROL_FIELD_KINDS = ["dropdown", "list", "input"] as const;
export type OpenControlFieldKind = (typeof OPEN_CONTROL_FIELD_KINDS)[number];

export function isOpenControlFieldKind(value: unknown): value is OpenControlFieldKind {
  return OPEN_CONTROL_FIELD_KINDS.includes(value as OpenControlFieldKind);
}

export type OpenFieldControl = {
  kind: OpenControlFieldKind;
  /** Null until the author picks a field ("not set up yet"). */
  dimension: string | null;
  selection: "single" | "multi";
  default: string[];
  label: string | null;
} & Partial<StoredControlSettings>;

// ── Display settings of a value control (plan 2026-10-09 button slicer,
// ADR-0374). The values come from `services/composition/controls.py`,
// GENERATED into shared-types (plan 2026-10-10 settings catalogue, A1): no
// copy here can drift from what the backend accepts. The backend stores a
// setting only when it differs from its default, so every one is optional
// on the stored spec; `controlSettings` gives the effective set.

const CONTROL_SPEC = COMPOSITION_SETTINGS.controls;
export const CONTROL_DISPLAYS = CONTROL_SPEC.displays;
export const CONTROL_LAYOUTS = CONTROL_SPEC.layouts;
export const CONTROL_SORTS = CONTROL_SPEC.sorts;
export const CONTROL_LABEL_POSITIONS = CONTROL_SPEC.label_positions;
export const CONTROL_COLUMNS_MIN = CONTROL_SPEC.bounds.columns[0];
export const CONTROL_COLUMNS_MAX = CONTROL_SPEC.bounds.columns[1];
/** The values endpoint's own cap (`MAX_VALUES_LIMIT`). */
export const CONTROL_LIMIT_MAX = CONTROL_SPEC.bounds.limit[1];
export const CONTROL_MAX_SELECTIONS_MIN = CONTROL_SPEC.bounds.max_selections[0];
/** The refresh request's value cap (`MAX_DEFAULT_VALUES`). */
export const CONTROL_MAX_SELECTIONS_MAX = CONTROL_SPEC.bounds.max_selections[1];

export type ControlDisplay = (typeof CONTROL_DISPLAYS)[number];
export type ControlLayout = (typeof CONTROL_LAYOUTS)[number];
export type ControlSort = (typeof CONTROL_SORTS)[number];
export type ControlLabelPosition = (typeof CONTROL_LABEL_POSITIONS)[number];

export type ControlSettings = {
  display: ControlDisplay;
  layout: ControlLayout;
  columns: number;
  sort: ControlSort;
  /** Above the values, or left of them (a column of controls lines up). */
  label_position: ControlLabelPosition;
  limit: number;
  select_all: boolean;
  required: boolean;
  search: boolean;
  /** Null = no cap. */
  max_selections: number | null;
  show_metric: boolean;
  relevant_only: boolean;
  /** A button list: Show top is the number of buttons, the rest of the
   *  values sit in a menu after them (plan 2026-10-16 more menu). */
  more_menu: boolean;
};
type StoredControlSettings = Omit<ControlSettings, "max_selections"> & { max_selections: number };

/** Each setting's default for `kind`: the backend's `setting_defaults`,
 *  generated. A fresh object per call - callers spread and patch it. */
export function controlSettingDefaults(kind: OpenControlFieldKind): ControlSettings {
  return { ...CONTROL_SPEC.defaults[kind] } as ControlSettings;
}

export type ControlSettingKey = keyof ControlSettings;
type SettingRule = { kinds: readonly string[]; display?: string; selection?: string };
const SETTING_APPLIES: Record<ControlSettingKey, SettingRule> = CONTROL_SPEC.applies;

/** Whether display setting `key` draws anything on a control of this kind,
 *  display and selection: the backend's `setting_applies`, over its
 *  generated table (plan 2026-10-19 a control reads back its effective
 *  settings, D2). The settings panel shows a setting exactly when it holds,
 *  and `settleDraft` drops the ones a change makes inapplicable. */
export function controlSettingApplies(
  key: ControlSettingKey,
  shape: { kind: string; display: string; selection: string },
): boolean {
  const rule = SETTING_APPLIES[key];
  if (!rule.kinds.includes(shape.kind)) return false;
  if (rule.display !== undefined && shape.display !== rule.display) return false;
  return rule.selection === undefined || shape.selection === rule.selection;
}

const ENUM_SETTINGS = {
  display: CONTROL_DISPLAYS,
  layout: CONTROL_LAYOUTS,
  sort: CONTROL_SORTS,
  label_position: CONTROL_LABEL_POSITIONS,
} as const;
const BOOL_SETTINGS = ["select_all", "required", "search", "show_metric", "relevant_only", "more_menu"] as const;
const NUMBER_SETTINGS = [
  ["columns", CONTROL_COLUMNS_MIN, CONTROL_COLUMNS_MAX],
  ["limit", 1, CONTROL_LIMIT_MAX],
  ["max_selections", CONTROL_MAX_SELECTIONS_MIN, CONTROL_MAX_SELECTIONS_MAX],
] as const;

/** The settings a raw stored control carries, read defensively: copy and
 *  seed paths write specs without the backend's validator, and these values
 *  end up in CSS and requests. An unknown enum or a non-boolean is dropped
 *  (its default applies), a number is rounded and clamped into its bounds. */
function storedSettingsOf(control: Record<string, unknown>): Partial<StoredControlSettings> {
  const out: Record<string, unknown> = {};
  for (const [key, allowed] of Object.entries(ENUM_SETTINGS)) {
    if ((allowed as readonly unknown[]).includes(control[key])) out[key] = control[key];
  }
  for (const key of BOOL_SETTINGS) {
    if (typeof control[key] === "boolean") out[key] = control[key];
  }
  for (const [key, low, high] of NUMBER_SETTINGS) {
    const raw = control[key];
    if (typeof raw === "number" && Number.isFinite(raw)) out[key] = Math.min(high, Math.max(low, Math.round(raw)));
  }
  return out as Partial<StoredControlSettings>;
}

/** The settings a control renders with: its stored ones (clamped again, a
 *  draft may not have come through `openFieldControlOfSpec`) over the kind's
 *  defaults, with the combinations the backend refuses neutralised rather
 *  than half-applied - All only on a multi control that is not required and
 *  has no cap, a cap only on a multi control, `required` only with a
 *  default to fall back to. */
export function controlSettings(control: OpenFieldControl): ControlSettings {
  const s: ControlSettings = {
    ...controlSettingDefaults(control.kind),
    ...storedSettingsOf(control as unknown as Record<string, unknown>),
  };
  const multi = control.selection === "multi";
  s.required = s.required && control.default.length > 0;
  s.max_selections = multi ? s.max_selections : null;
  s.select_all = s.select_all && multi && !s.required && s.max_selections === null;
  // A more menu is a button list's (the backend widens the query only there).
  s.more_menu = s.more_menu && control.kind === "list" && s.display === "buttons";
  return s;
}

/** The open field control a spec stores (ADR-0350), or null for anything
 *  else: a register control, a date range or button control, a chart. The
 *  backend's shape check wrote it; this reads it defensively all the same. */
export function openFieldControlOfSpec(spec: Record<string, unknown> | null | undefined): OpenFieldControl | null {
  const control = controlOfSpec(spec);
  if (!control) return null;
  const kind = control.kind;
  if (!OPEN_CONTROL_FIELD_KINDS.includes(kind as OpenControlFieldKind)) return null;
  if (!isOpenSelection(control.selection)) return null;
  const dimension = typeof control.dimension === "string" && control.dimension !== "" ? control.dimension : null;
  const raw = Array.isArray(control.default) ? control.default : [];
  return {
    kind: kind as OpenControlFieldKind,
    dimension,
    selection: control.selection,
    default: raw.filter((v): v is string => typeof v === "string" && v !== ""),
    label: typeof control.label === "string" && control.label !== "" ? control.label : null,
    ...storedSettingsOf(control),
  };
}

/** Every kind of open control an author can add, in the order the **Add a
 *  control** menu lists them (ADR-0350 Phase A). Mirrors the backend's
 *  `services/composition/controls.py::OPEN_CONTROL_KINDS`; a kind joins both
 *  lists together with its renderer. Whether the menu shows at all is the
 *  register's `offered` flag on the control block, read through the page's
 *  block types like every other Insert control. */
export const OPEN_CONTROL_KINDS = CONTROL_SPEC.kinds;
export type OpenControlKind = (typeof OPEN_CONTROL_KINDS)[number];

/** The kinds the **Add a control** menu offers today. The search box is
 *  held back until its use next to the dropdown's own search is clear
 *  (Arjen 2026-10-06). A stored one still renders. Date range joined with
 *  its Applies to (plan step A4b). */
const NOT_IN_MENU_YET: readonly OpenControlKind[] = ["input"];
export const MENU_CONTROL_KINDS: readonly OpenControlKind[] = OPEN_CONTROL_KINDS.filter(
  (k) => !NOT_IN_MENU_YET.includes(k),
);

export const OPEN_CONTROL_KIND_LABELS: Record<OpenControlKind, string> = {
  dropdown: "Dropdown",
  list: "Value picker",
  input: "Search box",
  date_range: "Date range",
  button: "Button",
};

export const OPEN_CONTROL_KIND_DESCRIPTIONS: Record<OpenControlKind, string> = {
  dropdown: "Pick values from a list that opens on click.",
  list: "Values as checkboxes or buttons, always visible.",
  input: "Type to find a value and pick it.",
  date_range: "Sets the period of the charts it applies to.",
  button: "Resets this page's filters, or opens a link.",
};

/** The cell a new control of this kind is created at. Within the control
 *  block's register size (`COMPOSITION_LAYOUT.blocks.control`, min 2x2):
 *  a list shows its values, so it is taller; a button is one compact line.
 *  A date range needs the period picker's width (browser gate A4b: at 2
 *  columns the picker ran out of its cell) and a row for the line that says
 *  which charts it reaches. The others take the register's default. */
export function controlKindSize(kind: OpenControlKind): GridSize {
  const spec = COMPOSITION_LAYOUT.blocks.control;
  if (kind === "list") return { w: spec.default.w, h: spec.default.h * 3 };
  if (kind === "date_range") return { w: spec.default.w, h: spec.default.h + 1 };
  if (kind === "button") return { w: spec.min.w, h: spec.min.h };
  return { w: spec.default.w, h: spec.default.h };
}

/** The spec of a new open control of this kind: the shape the backend's
 *  `controls.open_control_shape` accepts, with nothing chosen yet. A field
 *  kind starts without a field (the `pick_field` state, which the settings
 *  panel resolves), a list as multi-select, a dropdown and a search box as
 *  single; a button resets the page's filters until it is given a link; a
 *  date range carries nothing. No `interactions`: absent is `auto`, the
 *  whole page (a second date range on a page is stored on no chart by the
 *  backend, `controls.judge_and_clean_control`). */
export function newControlSpec(kind: OpenControlKind): Record<string, unknown> {
  if (kind === "date_range") return { chart_type: CONTROL_CHART_TYPE, control: { kind } };
  if (kind === "button") return { chart_type: CONTROL_CHART_TYPE, control: { kind, button: { action: "reset_filters" } } };
  const control: Record<string, unknown> = {
    kind, dimension: null, selection: kind === "list" ? "multi" : "single", default: [],
  };
  // A new dropdown or value picker offers the top 10 values (Arjen,
  // 2026-10-10). Stored on the spec, not as the shape's default: controls
  // placed before keep their 200.
  if (kind === "dropdown" || kind === "list") control.limit = NEW_CONTROL_LIMIT;
  return { chart_type: CONTROL_CHART_TYPE, control };
}

/** "Show top" of a newly placed dropdown or value picker. */
export const NEW_CONTROL_LIMIT = 10;

export function isPlaceholderSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return spec?.chart_type === "placeholder";
}

export function isTextBlockSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return isTextBlockType(spec?.chart_type);
}

/** The markdown or heading line a text block carries. Never null. */
export function blockContent(spec: Record<string, unknown> | null | undefined): string {
  const content = spec?.content;
  return typeof content === "string" ? content : "";
}


/** Block <-> query intent (mirrors services/composition/blocks.py); one
 *  table, owned by the API module. */
export { BLOCK_INTENTS };

export function blockFitsIntent(blockType: DataBlockType, intent: unknown): boolean {
  return typeof intent === "string" && BLOCK_INTENTS[blockType].includes(intent);
}

/** The block a query of this intent naturally lands in: a single value is a
 *  KPI, anything with an axis is a chart. Null for an unknown intent. */
export function fittingBlockType(intent: unknown): DataBlockType | null {
  if (intent === "single-value") return "kpi";
  if (intent === "time-series" || intent === "categorical") return "chart";
  return null;
}

/**
 * The block type a CONFIGURED tile is edited as, derived from its rendered
 * chart type.
 *
 * A dashboard item carries no `block_type` column: the block lives in
 * `chart_spec_json` only while a tile is still a placeholder. So for every
 * saved tile - including the ones the general chat wrote before blocks
 * existed - the rendered chart type is the ONLY signal of which editor form
 * fits it. `metric_card` is a KPI, `table` and `kpi_period_table` are a
 * table, everything else is a chart.
 */
export function blockTypeFromChartType(chartType: unknown): DataBlockType {
  if (isKpiChartType(chartType)) return "kpi";
  if (typeof chartType === "string" && (TABLE_CHART_TYPES as readonly string[]).includes(chartType)) return "table";
  return "chart";
}

